import type { FC } from 'react'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import classNames from 'classnames'
import type { FundingRequestPrivateDto } from '@/api/kubik/credit'
import { checkUserExistByPhone, useGetLegalDocuments } from '@/api/kubik/monolith'
import { useApplyFinancingOffer, useApplyLostFinancingOffer } from '@/components/composed/financing/hooks'
import { RegistrationForm } from '@/components/composed/RegistrationForm'
import { Button } from '@/components/ui'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { Checkbox, Dropdown, TextInput } from '@/components/ui/ReactHookFormFields'
import { getUser, isUserAgrarian } from '@/logic/auth/reducer'
import type { IApplyingFinancingOfferRequestBody } from '@/logic/financing/interfaces'
import { getLegalDocumentUrl } from '@/logic/legalDocuments/helpers'
import { useGtm } from '@/logic/metrika/financing/gtmHelpers'
import { EGtmBlock, EGtmLabel } from '@/logic/metrika/financing/types'
import type { RootState } from '@/redux/interfaces'
import { EFinancingOfferType, EPriceCurrency } from '@/types'
import type { ILegalDocument } from '@/types/legalDocuments'
import { ELegalDocumentType } from '@/types/legalDocuments'
import { formatLocalizedString, normalizePhoneNumber } from '@/utils/formatUtils'
import { useApplyingFinancingOfferForm } from '@/utils/hooks/applyingFinancingOffer/useApplyingFinancingOfferForm'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { messages } from './messages'
import type { IConsultingFormProps, IConsultingFormValues } from './types'
import { createConsultingRequestBody } from './utils'
import './styles.scss'

let consultingFormInstanceCounter = 0

const mapState = (state: RootState) => ({
  isAgrarian: isUserAgrarian()(state),
  user: getUser()(state),
})

const ConsultingForm: FC<IConsultingFormProps> = props => {
  const {
    className,
    purpose,
    purposeOptions,
    gtmProductName,
    firstInputRef,
    inputModifiers,
    headingVariant,
    registrationFormClassName,
    shouldSkipSuccessModal = false,
    onOpenRegistrationForm,
    onChangeLoginDialogOpen,
    onSuccessFormSubmit,
    onErrorFormSubmit,
  } = props
  const { isAgrarian, user } = useSelector(mapState)
  const { applyFinancingOffer, isLoading } = useApplyFinancingOffer(EGtmBlock.CONSULTATION)
  const { applyLostFinancingOffer } = useApplyLostFinancingOffer()
  const { isAuthenticated, openAuthModal } = useAuthenticateUser()
  const { pushGtmLoanProductModalAuth, pushGtmProduct, pushGtmFormInteraction } = useGtm()
  const { data: getLegalDocumentsResult } = useGetLegalDocuments()
  const legalDocuments = (getLegalDocumentsResult?.data ?? []) as ILegalDocument[]
  const { isMobile } = useDeviceType()

  const [showRegistration, setShowRegistration] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isPhoneCheckLoading, setIsPhoneCheckLoading] = useState(false)

  const handleSuccessFinancingOfferFormSending = useCallback(
    (fundingRequests: FundingRequestPrivateDto[]) => {
      const firstFundingRequestId = fundingRequests?.[0]?.id

      if (firstFundingRequestId) {
        onSuccessFormSubmit?.(firstFundingRequestId)
      }
    },
    [onSuccessFormSubmit],
  )

  const { setOptions, profile, formValues } = useApplyingFinancingOfferForm({
    shouldSkipSuccessModal,
    productId: FundingProduct.MARKETPLACE,
    offerType: EFinancingOfferType.MARKETPLACE,
    onSuccess: handleSuccessFinancingOfferFormSending,
    onError: onErrorFormSubmit,
  })

  const initialValues = useMemo<IConsultingFormValues>(
    () => ({
      loanAmount: formValues.loanAmount?.value.toString() || '',
      purpose: purpose || formValues.comment || '',
      phone: profile.phone,
      agreement: false,
    }),
    [formValues, profile.phone, purpose],
  )

  const sendLostData = (data: Partial<IApplyingFinancingOfferRequestBody>) => {
    const productId = data.productId ?? FundingProduct.MARKETPLACE

    const defaultLoanAmount = {
      value: 0,
      uom: 'RUB',
    }

    const body = {
      agreement: true,
      email: data.email ?? '',
      phone: data.phone ?? '',
      comment: data.comment ?? '',
      loanAmount: data.loanAmount ?? defaultLoanAmount,
      productId,
    }

    applyLostFinancingOffer({ body })
  }

  const { control, handleSubmit, setValue } = useForm<IConsultingFormValues>({ defaultValues: initialValues, mode: 'onChange' })

  const instanceIdRef = useRef<number | null>(null)

  if (instanceIdRef.current === null) {
    // eslint-disable-next-line no-plusplus
    instanceIdRef.current = ++consultingFormInstanceCounter
  }

  const formInstanceId = instanceIdRef.current

  const heading = headingVariant !== undefined ? messages.headings[headingVariant] : undefined

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setValue('phone', normalizePhoneNumber(profile.phone)), [profile.phone])

  const onClickSubmit = () => {
    if (gtmProductName) pushGtmProduct(gtmProductName, EGtmLabel.ACTION)
  }

  const onFormInteraction = (eventLabel: string) => () => {
    if (gtmProductName) pushGtmFormInteraction(gtmProductName, eventLabel)
  }

  const onSubmit = async (values: IConsultingFormValues) => {
    const body = createConsultingRequestBody({ values, user, productId: FundingProduct.MARKETPLACE })

    const sendForm = async () => {
      try {
        const fundingRequests = await applyFinancingOffer({ body, withSuccessModal: !shouldSkipSuccessModal })
        const firstFundingRequestId = fundingRequests?.[0]?.id

        if (firstFundingRequestId && onSuccessFormSubmit) {
          onSuccessFormSubmit(firstFundingRequestId)
        }
      } catch {
        onErrorFormSubmit?.()
      }
    }

    if (isAuthenticated) {
      sendForm()
    } else {
      if (gtmProductName) pushGtmLoanProductModalAuth(gtmProductName)

      setIsPhoneCheckLoading(true)
      const checkUserResult = await checkUserExistByPhone({ phone: normalizePhoneNumber(values.phone) })
      setIsPhoneCheckLoading(false)

      if (checkUserResult.data?.exist) {
        onChangeLoginDialogOpen?.({ isOpen: true, phoneNumber: values.phone })

        openAuthModal({
          shouldSendCodeImmediately: true,
          shouldSkipSelectRoleStep: true,
          initialValues: { phone: normalizePhoneNumber(values.phone) },
          onCloseDialog: () => {
            setOptions({
              formValues: createConsultingRequestBody({ values, user, productId: FundingProduct.MARKETPLACE }),
            })

            onChangeLoginDialogOpen?.({ isOpen: false, phoneNumber: values.phone })
          },
          onCloseModal: () => sendLostData(body),
        })
      } else {
        setPhoneNumber(values.phone)
        setShowRegistration(true)
        onOpenRegistrationForm?.(values.phone)
      }
    }
  }

  const afterRegistrationComplete = () => {
    handleSubmit(onSubmit)()
    setShowRegistration(false)
  }

  if (showRegistration) {
    return (
      <RegistrationForm
        className={registrationFormClassName}
        phoneNumber={phoneNumber}
        goBack={() => {
          setShowRegistration(false)
        }}
        afterRegistrationComplete={afterRegistrationComplete}
        shouldSkipSelectRoleStep
      />
    )
  }

  return (
    <div className={classNames('consultingForm', className)}>
      {heading && (
        <div className="consultingForm__heading">
          <h3 className="consultingForm__headingTitle">{heading.title}</h3>
          <p>{heading.description}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="consultingForm__inputs">
          <TextInput
            name="loanAmount"
            id={`loanAmount-${formInstanceId}`}
            type="price"
            placeholder={messages.loanAmount}
            rules={{
              required: messages.error.required,
              min: { value: 1, message: messages.error.incorrectPrice },
            }}
            onFocus={onFormInteraction('loanAmount')}
            control={control}
            shouldBeTouchedBeforeError={false}
            isRequired
            inputRef={firstInputRef}
            inputMode="numeric"
            modifiers={inputModifiers}
          >
            <span className="uom">{EPriceCurrency.RUB}</span>
          </TextInput>

          {purposeOptions && (
            <Dropdown
              id={`purpose-${formInstanceId}`}
              name="purpose"
              placeholder={messages.purpose}
              options={purposeOptions}
              rules={{ required: messages.error.required }}
              onFocus={onFormInteraction('loanAmount')}
              control={control}
              modifiers={inputModifiers}
              shouldBeTouchedBeforeError={false}
              isRequired
            />
          )}

          <TextInput
            id={`phone-${formInstanceId}`}
            name="phone"
            type="tel"
            inputMode="numeric"
            isReadonly={isAuthenticated}
            placeholder={messages.phone}
            rules={{ required: messages.error.required }}
            onFocus={onFormInteraction('loanAmount')}
            modifiers={inputModifiers}
            control={control}
            shouldBeTouchedBeforeError={false}
            isRequired
          />
        </div>

        <div className="consultingForm__agreement">
          <Checkbox
            labelTextClassName="consultingForm__agreementLabel"
            control={control}
            id="agreement"
            name="agreement"
            isMobile={isMobile}
            rules={{ required: messages.agreement.required }}
            errorClassName="consultingForm__agreementError"
          >
            <div>
              {formatLocalizedString(messages.agreement.text, {
                agreement: (
                  <CommonLink
                    url={getLegalDocumentUrl(legalDocuments, ELegalDocumentType.personalDataConsent)}
                    shouldOpenInNewWindow
                    className="underline underline_green color_green"
                  >
                    {messages.agreement.agreement}
                  </CommonLink>
                ),
                policy: (
                  <CommonLink
                    url={getLegalDocumentUrl(legalDocuments, ELegalDocumentType.registrationPolicy)}
                    shouldOpenInNewWindow
                    className="underline underline_green color_green"
                  >
                    {messages.agreement.policy}
                  </CommonLink>
                ),
              })}
            </div>
          </Checkbox>
        </div>

        <Button
          type="submit"
          className="consultingForm__submit"
          modifiers={['green', 'full-width-always']}
          onClick={onClickSubmit}
          loaderProps={{ isVisible: isLoading || isPhoneCheckLoading }}
          isDisabled={!isAgrarian && isAuthenticated}
        >
          {messages.submit}
        </Button>
      </form>
    </div>
  )
}

export default memo(ConsultingForm)
